// elasticity.ts — capex beta / rate beta via ±perturbation (Phase 0.3).
// Direct port of the prototype's elast(). Pure: takes drivers + world.

import type { Drivers, World } from "./entities.ts";
import { solve } from "./solve.ts";

/**
 * Revenue elasticity of a named supplier to `key`, computed by perturbing it
 * ±bump around the given drivers. `rel` = relative bump (multiplicative) vs.
 * absolute. A y10 bump is applied in parallel across y20/y30 (curve shift).
 */
export function elast(
  name: string,
  key: keyof Drivers,
  bump: number,
  rel: boolean,
  st: Drivers,
  w: World,
): number {
  const up: Drivers = { ...st };
  const dn: Drivers = { ...st };
  if (rel) {
    up[key] = st[key] * (1 + bump);
    dn[key] = st[key] * (1 - bump);
  } else {
    up[key] = st[key] + bump;
    dn[key] = st[key] - bump;
  }
  if (key === "y10") {
    up.y20 = st.y20 + bump;
    up.y30 = st.y30 + bump;
    dn.y20 = st.y20 - bump;
    dn.y30 = st.y30 - bump;
  }
  const ru = solve(up, w);
  const rd = solve(dn, w);
  const r0 = solve(st, w);
  const f = (res: ReturnType<typeof solve>): number | null => {
    for (const s of res.sup) if (s.n === name) return s.rev;
    return null;
  };
  const a = f(ru);
  const b = f(rd);
  const c = f(r0);
  if (a === null || b === null || c === null || c === 0) return 0;
  if (key === "capex") {
    const dCap = (ru.cx - rd.cx) / r0.cx;
    if (Math.abs(dCap) < 1e-9) return 0;
    return (a - b) / c / dCap;
  }
  return (((a - b) / c) / 2) * 100;
}

/** Revenue elasticity to system capex (±10%). Matches the prototype's beta chart. */
export function capexBeta(name: string, st: Drivers, w: World): number {
  return elast(name, "capex", 0.1, true, st, w);
}

/** Revenue sensitivity to a 100bp parallel curve shift (±0.5 → 100bp span). */
export function rateBeta(name: string, st: Drivers, w: World): number {
  return elast(name, "y10", 0.5, false, st, w);
}
